/**
 * 🔍 UTM Tracking Debugger for Production
 * 
 * This utility helps diagnose UTM tracking issues in production environments.
 * Use window.debugUTM() in browser console to get detailed tracking information.
 */

import { 
  getUtmParamsForApi, 
  getStoredUtmParams, 
  extractUtmParams,
  getStorageInfo,
  isPrivateBrowsing
} from './utmTracker';

/**
 * Comprehensive UTM debugging function for production use
 */
export const debugUTMTracking = async () => {
  const startTime = performance.now();
  
  try {
    // 1. Get current URL UTM parameters
    const urlUtmData = extractUtmParams();
    
    // 2. Get stored UTM data
    const storedUtmData = getStoredUtmParams();
    
    // 3. Get API-ready UTM data
    const apiUtmData = getUtmParamsForApi();
    
    // 4. Check storage capabilities
    const storageInfo = getStorageInfo();
    
    // 5. Check private browsing status
    const privateBrowsing = await isPrivateBrowsing().catch(() => false);
    
    // 6. Analyze the current state
    const analysis = analyzeUTMState({
      urlUtmData,
      storedUtmData,
      apiUtmData,
      storageInfo,
      privateBrowsing
    });
    
    const endTime = performance.now();
    
    const debugReport = {
      timestamp: new Date().toISOString(),
      execution_time_ms: Math.round(endTime - startTime),
      current_page: {
        url: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search,
        referrer: document.referrer,
        title: document.title
      },
      utm_data: {
        from_url: urlUtmData,
        stored: storedUtmData,
        api_ready: apiUtmData
      },
      storage_capabilities: storageInfo,
      browser_info: {
        user_agent: navigator.userAgent,
        private_browsing: privateBrowsing,
        cookies_enabled: navigator.cookieEnabled,
        local_storage_available: !!window.localStorage,
        session_storage_available: !!window.sessionStorage,
        is_safari: /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent),
        is_mobile: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent)
      },
      analytics_platforms: {
        google_analytics: {
          gtag_available: typeof window.gtag === 'function',
          google_ads_id: process.env.REACT_APP_GOOGLE_ADS_ID ? 'Configured' : 'Missing',
          data_layer_length: window.dataLayer ? window.dataLayer.length : 0
        },
        facebook_pixel: {
          fbq_available: typeof window.fbq === 'function',
          pixel_id: process.env.REACT_APP_FACEBOOK_PIXEL_ID ? 'Configured' : 'Missing'
        },
        hotjar: {
          hj_available: typeof window.hj === 'function'
        }
      },
      analysis: analysis,
      debugging_tips: generateDebuggingTips(analysis),
      test_commands: getTestCommands()
    };
    
    // Format for console display
    console.group('🔍 UTM Tracking Debug Report');
    console.log('📊 Summary:', analysis.summary);
    console.log('⚠️  Issues Found:', analysis.issues.length);
    
    if (analysis.issues.length > 0) {
      console.group('❌ Issues:');
      analysis.issues.forEach((issue, index) => {
        console.log(`${index + 1}. ${issue}`);
      });
      console.groupEnd();
    }
    
    if (analysis.recommendations.length > 0) {
      console.group('💡 Recommendations:');
      analysis.recommendations.forEach((rec, index) => {
        console.log(`${index + 1}. ${rec}`);
      });
      console.groupEnd();
    }
    
    console.log('📋 Full Debug Data:', debugReport);
    console.log('🧪 Test Commands:', debugReport.test_commands);
    console.groupEnd();
    
    return debugReport;
    
  } catch (error) {
    console.error('Error in UTM debugging:', error);
    return {
      error: true,
      message: error.message,
      timestamp: new Date().toISOString()
    };
  }
};

/**
 * Analyze the current UTM tracking state and identify issues
 */
function analyzeUTMState({ urlUtmData, storedUtmData, apiUtmData, storageInfo, privateBrowsing }) {
  const issues = [];
  const recommendations = [];
  let summary = '';
  
  // Check URL UTM parameters
  if (urlUtmData) {
    summary += '✅ URL has UTM parameters. ';
  } else {
    summary += '⚠️ No UTM parameters in current URL. ';
    if (!storedUtmData) {
      issues.push('No UTM parameters found in URL and no stored data available');
      recommendations.push('Ensure marketing links include UTM parameters like ?utm_source=google&utm_campaign=test');
    }
  }
  
  // Check stored UTM data
  if (storedUtmData) {
    summary += '✅ Stored UTM data available. ';
    
    // Check if stored data is recent
    const age = Date.now() - (storedUtmData.timestamp || 0);
    const daysOld = Math.floor(age / (24 * 60 * 60 * 1000));
    
    if (daysOld > 30) {
      issues.push(`Stored UTM data is ${daysOld} days old and may expire soon`);
      recommendations.push('UTM data expires after 30 days. Fresh marketing traffic needed.');
    }
  } else {
    summary += '❌ No stored UTM data. ';
    if (!urlUtmData) {
      issues.push('No UTM data available from URL or storage');
    }
  }
  
  // Check API-ready data
  if (apiUtmData) {
    summary += '✅ API-ready UTM data available.';
  } else {
    summary += '❌ No API-ready UTM data.';
    issues.push('No UTM data available for API transmission');
  }
  
  // Check storage capabilities
  if (!storageInfo.localStorage) {
    issues.push('localStorage not available');
    recommendations.push('Browser may be in private mode or have storage disabled');
  }
  
  if (privateBrowsing) {
    issues.push('Private browsing mode detected');
    recommendations.push('UTM tracking may be limited in private browsing mode');
  }
  
  if (storageInfo.usingFallback) {
    issues.push('Using fallback storage method');
    recommendations.push('Primary storage methods failed, using less reliable fallback');
  }
  
  // Check for common patterns
  if (urlUtmData && !storedUtmData) {
    issues.push('UTM parameters in URL but not being stored properly');
    recommendations.push('Check for storage errors or privacy settings blocking data persistence');
  }
  
  return {
    summary,
    issues,
    recommendations,
    has_utm_data: !!apiUtmData,
    storage_working: storageInfo.localStorage && !storageInfo.usingFallback,
    private_browsing: privateBrowsing
  };
}

/**
 * Generate specific debugging tips based on analysis
 */
function generateDebuggingTips(analysis) {
  const tips = [];
  
  if (!analysis.has_utm_data) {
    tips.push('🔗 Test with: window.location.href = "?utm_source=test&utm_campaign=debug"');
    tips.push('📱 Check marketing links have UTM parameters');
    tips.push('🔄 Try refreshing after adding UTM parameters to URL');
  }
  
  if (!analysis.storage_working) {
    tips.push('🔒 Check if browser is in private/incognito mode');
    tips.push('🍪 Verify cookies and local storage are enabled');
    tips.push('🔧 Try in a different browser or device');
  }
  
  if (analysis.private_browsing) {
    tips.push('👤 Private browsing limits UTM persistence');
    tips.push('🔄 UTM data will only last for the current session');
  }
  
  tips.push('📞 Contact support with this debug report if issues persist');
  
  return tips;
}

/**
 * Provide test commands users can run in console
 */
function getTestCommands() {
  return {
    test_utm_extraction: 'extractUtmParams()',
    test_utm_storage: 'storeUtmParams({utm_source: "test", utm_campaign: "debug"})',
    test_utm_retrieval: 'getStoredUtmParams()',
    test_api_data: 'getUtmParamsForApi()',
    check_storage: 'getStorageInfo()',
    simulate_utm_url: 'window.location.href = "?utm_source=test&utm_campaign=console_debug"',
    clear_utm_data: 'clearUtmParams()',
    full_debug: 'debugUTM()'
  };
}

/**
 * Simulate UTM data for testing purposes
 */
export const simulateUTMData = (source = 'debug', campaign = 'test') => {
  const testData = {
    utm_source: source,
    utm_medium: 'debug',
    utm_campaign: campaign,
    utm_content: 'console_test',
    timestamp: Date.now(),
    first_seen_date: new Date().toISOString(),
    landing_page: window.location.href,
    referrer: 'debug_simulation'
  };
  
  try {
    localStorage.setItem('lumiwealth_utm_data', JSON.stringify(testData));
    console.log('✅ Test UTM data simulated:', testData);
    return testData;
  } catch (error) {
    console.error('❌ Failed to simulate UTM data:', error);
    return null;
  }
};

/**
 * Test the complete analytics flow
 */
export const testAnalyticsFlow = async () => {
  console.group('🧪 Testing Complete Analytics Flow');
  
  try {
    // 1. Simulate UTM data
    const testUtm = simulateUTMData('test_analytics', 'flow_test');
    console.log('1. ✅ UTM data simulated');
    
    // 2. Test retrieval
    const retrieved = getUtmParamsForApi();
    console.log('2. ✅ UTM data retrieved:', retrieved);
    
    // 3. Test analytics event (mock)
    const testEvent = {
      event: 'test_event',
      source: 'debug',
      ...retrieved
    };
    console.log('3. ✅ Analytics event prepared:', testEvent);
    
    // 4. Check tracking platforms
    const platforms = {
      gtag: typeof window.gtag === 'function',
      fbq: typeof window.fbq === 'function',
      dataLayer: Array.isArray(window.dataLayer)
    };
    console.log('4. ✅ Tracking platforms:', platforms);
    
    console.log('🎉 Analytics flow test completed successfully');
    
  } catch (error) {
    console.error('❌ Analytics flow test failed:', error);
  }
  
  console.groupEnd();
};

/**
 * Initialize debugging utilities on window object for production use
 */
export const initDebugging = () => {
  if (typeof window !== 'undefined') {
    window.debugUTM = debugUTMTracking;
    window.simulateUTM = simulateUTMData;
    window.testAnalytics = testAnalyticsFlow;
    
    // UTM debugging utilities are available via:
    // - window.debugUTM() - Complete UTM debug report
    // - window.simulateUTM(source, campaign) - Test UTM tracking
    // - window.testAnalytics() - Test complete analytics flow
  }
};

// Auto-initialize in browser environments
if (typeof window !== 'undefined') {
  initDebugging();
} 